import { useState, useEffect } from 'react';
import { db, doc, getDoc, setDoc, updateDoc, increment } from '../firebase';

export const useProjectStats = (projectId) => {
  const [stats, setStats] = useState({ likes: 0, views: 0, liked: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    
    fetchStats();
    trackView();
  }, [projectId]);

  const fetchStats = async () => {
    try {
      const statsRef = doc(db, 'projectStats', projectId);
      const statsDoc = await getDoc(statsRef);
      
      if (statsDoc.exists()) {
        const data = statsDoc.data();
        setStats({
          likes: data.likes || 0,
          views: data.views || 0,
          liked: checkIfLiked(projectId)
        });
      } else {
        // Initialize stats document
        await setDoc(statsRef, { likes: 0, views: 0, createdAt: new Date() });
        setStats({ likes: 0, views: 0, liked: false });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const trackView = async () => {
    try {
      const statsRef = doc(db, 'projectStats', projectId);
      
      // Check if already viewed in this session
      const viewedKey = `viewed_${projectId}`;
      if (sessionStorage.getItem(viewedKey)) return;
      
      // Increment view count
      await updateDoc(statsRef, {
        views: increment(1),
        lastViewed: new Date()
      });
      
      // Mark as viewed in session
      sessionStorage.setItem(viewedKey, 'true');
      
      setStats(prev => ({ ...prev, views: prev.views + 1 }));
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const toggleLike = async () => {
    try {
      const statsRef = doc(db, 'projectStats', projectId);
      const likedProjects = JSON.parse(localStorage.getItem('likedProjects') || '[]');
      const isLiked = likedProjects.includes(projectId);
      
      if (isLiked) {
        // Unlike
        await updateDoc(statsRef, {
          likes: increment(-1)
        });
        
        const updated = likedProjects.filter(id => id !== projectId);
        localStorage.setItem('likedProjects', JSON.stringify(updated));
        
        setStats(prev => ({ ...prev, likes: prev.likes - 1, liked: false }));
      } else {
        // Like
        await updateDoc(statsRef, {
          likes: increment(1),
          lastLiked: new Date()
        });
        
        likedProjects.push(projectId);
        localStorage.setItem('likedProjects', JSON.stringify(likedProjects));
        
        setStats(prev => ({ ...prev, likes: prev.likes + 1, liked: true }));
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const checkIfLiked = (projectId) => {
    const likedProjects = JSON.parse(localStorage.getItem('likedProjects') || '[]');
    return likedProjects.includes(projectId);
  };

  return { stats, loading, toggleLike };
};
